/**
 * Operational Validation — feedback capture model.
 *
 * @typedef {Object} FeedbackEntry
 * @property {string} id
 * @property {"issue"|"idea"} kind          'Report Issue' vs 'Suggest Improvement'
 * @property {string} body                   Free-form message from the user.
 * @property {string} title                  Short summary (optional, derived from body if empty).
 * @property {string} page                   pathname at submission time.
 * @property {string|null} teacherId         Currently-active teacher workspace id, or null.
 * @property {string|null} teacherName
 * @property {"teacher"|"admin"|null} role
 * @property {string} workspaceMode          'all' or a teacher id (admin context).
 * @property {string} createdAt              ISO timestamp.
 * @property {string} userAgent
 * @property {{width:number,height:number}} viewport
 * @property {string} appVersion             Hardcoded for now; will track build later.
 * @property {"pending"|"synced"|"resolved"} status
 *                                           pending = local-only, synced = pushed to Supabase
 *                                           (future), resolved = admin closed the loop.
 */

export const FEEDBACK_KINDS = ["issue", "idea"];

export const KIND_LABEL = {
    issue: "Report Issue",
    idea: "Suggest Improvement",
};

export const KIND_DESCRIPTION = {
    issue: "Something broken, confusing, or slower than it should be.",
    idea: "Something that would make daily teaching easier.",
};

const APP_VERSION = "tempo.0.6-internal";

export function buildFeedbackEntry({
    kind,
    body,
    title,
    page,
    teacher,
    workspaceMode,
}) {
    if (!FEEDBACK_KINDS.includes(kind)) {
        throw new Error(`Invalid feedback kind: ${kind}`);
    }
    const trimmedBody = (body || "").trim();
    if (!trimmedBody) throw new Error("Feedback body is required.");
    const trimmedTitle = (title || "").trim();
    const derivedTitle =
        trimmedTitle ||
        trimmedBody.split("\n")[0].slice(0, 60) +
            (trimmedBody.length > 60 ? "…" : "");
    return {
        id: `fb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
        kind,
        title: derivedTitle,
        body: trimmedBody,
        page: page || "/",
        teacherId: teacher?.id || null,
        teacherName: teacher?.name || null,
        role: teacher?.role || null,
        workspaceMode: workspaceMode || "all",
        createdAt: new Date().toISOString(),
        userAgent:
            (typeof navigator !== "undefined" && navigator.userAgent) || "unknown",
        viewport: {
            width: typeof window !== "undefined" ? window.innerWidth : 0,
            height: typeof window !== "undefined" ? window.innerHeight : 0,
        },
        appVersion: APP_VERSION,
        status: "pending",
    };
}

/**
 * Stub for the future Supabase sync. Returns the number of entries that would
 * be pushed. The real implementation will live here once auth is wired up.
 *
 * TODO(supabase): POST every entry where status === 'pending' to a
 *   `feedback` table, then flip status to 'synced' on success. Should be
 *   idempotent (use entry.id as upsert key).
 */
export async function syncFeedback(/* entries */) {
    return 0;
}
